import axios from 'axios'
import { appUrls } from '../config/ajax'
import Qs from 'qs'
import ArtistSearch from '@scripts/components/artistSearch'
import MonthYearPicker from '@scripts/components/monthYearPicker'

const isDev = false

class Releases {
  private monthYearPicker!: MonthYearPicker
  private artistSearch!: ArtistSearch

  public init = () => {
    this.monthYearPicker = new MonthYearPicker(this.fetchReleases.bind(this))
    this.artistSearch = new ArtistSearch(this.fetchReleases.bind(this), isDev)
    this.bindFilterButtons()
    this.bindClearAllButton()
    // Use delegated handler so the select keeps working across AJAX swaps
    document.removeEventListener('change', this.handleSortChangeDelegated)
    document.addEventListener('change', this.handleSortChangeDelegated)
  }

  private fetchReleases = async (): Promise<void> => {
    this.setInputsDisabled(true)
    this.stripPaginationFromUrl()

    const { rm, ry, artist, orderBy } = this.getQueryParams()
    const data = {
      action: 'fetch_releases',
      nonce: WP_AJAX.wpRestNonce,
      rm,
      ry,
      artist,
      orderBy,
    }

    try {
      const response = await axios.post<string>(String(appUrls.ajax), Qs.stringify(data))
      this.injectHtmlResponse(response.data)
    } catch (error) {
      console.error('Release fetch error:', error)
    } finally {
      this.setInputsDisabled(false)
    }
  }

  private injectHtmlResponse = (html: string): void => {
    const target = document.getElementById('releases-return')
    if (!target) return

    target.innerHTML = html
    target.classList.remove('animate-fadeIn')
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        target.classList.add('animate-fadeIn')
      })
    })

    this.bindFilterButtons()
    this.bindClearAllButton()
  }

  private getQueryParams = (): { rm: string; ry: string; artist: string; orderBy: string } => {
    const params = new URLSearchParams(window.location.search)
    return {
      rm: params.get('rm') || '',
      ry: params.get('ry') || '',
      artist: params.get('artist') || '',
      orderBy: params.get('orderBy') || '',
    }
  }

  private handleSortChangeDelegated = (e: Event): void => {
    const target = e.target as HTMLElement | null
    if (!target) return
    if (!(target instanceof HTMLSelectElement)) return
    if (!target.matches('select[data-sort-select="true"]')) return

    const val = target.value || ''
    const url = new URL(window.location.href)
    // default (empty) means remove orderBy
    if (!val) url.searchParams.delete('orderBy')
    else url.searchParams.set('orderBy', val)
    window.history.replaceState({}, '', url.toString())
    this.fetchReleases()
  }

  private bindFilterButtons = (): void => {
    const buttons = document.querySelectorAll<HTMLButtonElement>('.prp-remove-filter')
    buttons.forEach((button) => {
      button.removeEventListener('click', this.handleFilterButton)
      button.addEventListener('click', this.handleFilterButton)
    })
  }

  private handleFilterButton = (e: MouseEvent): void => {
    const button = e.currentTarget as HTMLButtonElement
    const param = button.dataset.queryParam
    if (!param) return

    const url = new URL(window.location.href)
    url.searchParams.delete(param)
    window.history.replaceState({}, '', url.toString())

    this.fetchReleases()
  }

  private bindClearAllButton = (): void => {
    const clearBtn = document.getElementById('prp-clear-all-filters')
    if (clearBtn) {
      clearBtn.removeEventListener('click', this.handleClearAllFilters)
      clearBtn.addEventListener('click', this.handleClearAllFilters)
    }
  }

  private handleClearAllFilters = (): void => {
    const url = new URL(window.location.href)
    url.searchParams.delete('artist')
    url.searchParams.delete('rm')
    url.searchParams.delete('ry')
    url.searchParams.delete('orderBy')
    window.history.replaceState({}, '', url.toString())

    // Reset UI controls
    const artistInput = document.querySelector<HTMLInputElement>('#artist-search')
    const monthSelect = document.querySelector<HTMLSelectElement>('#month-select')
    const yearSelect = document.querySelector<HTMLSelectElement>('#year-select')
    const clearButton = document.querySelector<HTMLButtonElement>('#artist-search-clear')

    if (artistInput) {
      artistInput.value = ''
      artistInput.classList.remove('animate-inputLoading')
    }

    if (clearButton) {
      clearButton.classList.add('hidden')
      clearButton.classList.remove('flex')
    }

    if (monthSelect) monthSelect.selectedIndex = 0
    if (yearSelect) yearSelect.selectedIndex = 0
    const sortSelect = document.querySelector<HTMLSelectElement>('select[data-sort-select="true"]')
    if (sortSelect) sortSelect.selectedIndex = 0

    this.fetchReleases()
  }

  private setInputsDisabled = (disabled: boolean): void => {
    const artistInput = document.querySelector<HTMLInputElement>('#artist-search')
    const monthSelect = document.querySelector<HTMLSelectElement>('#month-select')
    const yearSelect = document.querySelector<HTMLSelectElement>('#year-select')

    artistInput?.toggleAttribute('disabled', disabled)
    monthSelect?.toggleAttribute('disabled', disabled)
    yearSelect?.toggleAttribute('disabled', disabled)
  }

  private stripPaginationFromUrl = (): void => {
    const url = new URL(window.location.href)
    const pathname = url.pathname.replace(/\/page\/\d+\/?$/, '/')
    url.pathname = pathname.endsWith('/') ? pathname : pathname + '/'
    window.history.replaceState({}, '', url.toString())
  }
}

export default Releases
